/* eslint-disable ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
// Extends from
import Component from '@ember/component';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// Internal imports
import NotificationTransportType from 'linced-app/enums/notification-transport-type';
import ResultStatus from 'linced-app/enums/result-status';
import TrackingStatus from 'linced-app/enums/tracking-status';
import {
  isPushNotification,
  isEmailNotification,
  getNotificationTypeLabel,
} from 'linced-app/utils/notification-transports';
// External imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import PersonNotificationPreference from '@linc-technologies/linced-common/enums/person-option-notification-preference';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import PostStatus from '@linc-technologies/linced-common/enums/post-status';
import PostType from '@linc-technologies/linced-common/enums/post-type';
import moment from 'moment';
import { filterValue } from '@linc-technologies/linced-common/utils/filtering';
import { pluralize } from '@linc-technologies/linced-common/utils/inflector';
import {
  byKeys,
  compact,
  filterBy,
  findBy,
  mapBy,
  rejectBy,
  resolved,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';

export default class PostAnalysis extends Component {
  // Services
  @service
  personOptions;

  @service
  session;

  @service
  store;

  PostTypes = PostType;

  // Properties
  // Default table views
  @tracked
  externalRecipientsView = 'learners';

  @tracked
  nonRecipientsView = 'learners';

  // Computed properties
  @computed('post.{status,postType,page.visibleToParents}')
  get showCaregiverNotifications() {
    // Only posts visible to parents should show certain sections
    // This means Published Community Notices + Published Posts on Pages visible to parents
    // (Any Community Notice we're analysing must be published)
    let isCommunityNotice = this.post.postType === PostType.COMMUNITY_NOTICE;
    let isPost = this.post.postType === PostType.POST;
    let isVisibleToParents = this.post.page.get('visibleToParents');
    let hasBeenPublished =
      this.post.status === PostStatus.PUBLISHED ||
      this.post.status === PostStatus.UNPUBLISHED;
    let isVisiblePublishedPost =
      isPost && isVisibleToParents && hasBeenPublished;
    return isCommunityNotice || isVisiblePublishedPost;
  }

  @computed('notificationResults.@each.{transportType,isRead,email}')
  get uniqNotificationResults() {
    return uniqBy(
      rejectBy(this.notificationResults, 'transportType', 'unspecified')
        .map((result) => {
          return {
            email: result.person.get('fields.core:emailAddress.valueString'),
            notificationType: result.transportType,
            recipientType: result.personType,
            isRead: result.isRead,
            trackingStatus: result.trackingStatus,
          };
        })
        .sort(byKeys('isRead'))
        .reverse(),
      'email',
    );
  }

  @computed('notificationsResults.@each.{transportType,status}')
  get allReceivedResults() {
    return filterBy(
      rejectBy(this.notificationResults, 'transportType', 'unspecified'),
      'status',
      ResultStatus.SUCCESS,
    ).map((result) => {
      return {
        email: result.person.get('fields.core:emailAddress.valueString'),
        notificationType: result.transportType,
        recipientType: result.personType,
        isRead: result.isRead,
        trackingStatus: result.trackingStatus,
        person: result.get('person'),
      };
    });
  }

  @computed('allReceivedResults.@each.notificationType')
  get typeChartData() {
    let receiptTypes = mapBy(
      uniqBy(this.allReceivedResults, 'notificationType'),
      'notificationType',
    );
    let arr = [];
    receiptTypes.forEach((type) => {
      let label = getNotificationTypeLabel(type);
      let count = filterBy(
        this.allReceivedResults,
        'notificationType',
        type,
      ).length;
      arr.push({
        value: type,
        label,
        count,
        tooltip: `${label} (${count})`,
      });
    });
    return arr;
  }

  @computed('uniqNotificationResults.@each.recipientType')
  get recipientChartData() {
    let receiptTypes = mapBy(
      uniqBy(this.uniqNotificationResults, 'recipientType'),
      'recipientType',
    );
    let arr = [];
    receiptTypes.forEach((type) => {
      let label = PersonType.getLabel(parseInt(type, 10));
      let count = filterBy(
        this.uniqNotificationResults,
        'recipientType',
        type,
      ).length;
      arr.push({
        value: type,
        label,
        count,
        tooltip: `${label} (${count})`,
      });
    });
    return arr;
  }

  @computed('allReceivedResults.@each.{isRead,email,trackingStatus}')
  get readChartData() {
    let results = this.allReceivedResults;
    // Some people can have results for multiple notification types.
    // One can be read, one can be unread.
    // If one type is read, then it's considered the person's read the post altogether
    let read = new Set();
    let opened = new Set();
    let total = new Set();
    results.forEach((result) => {
      total.add(result.email);
      if (this.checkIsResultRead(result)) {
        read.add(result.email);
        // Remove from "opened" list, to avoid double-counting.
        opened.delete(result.email);
      } else if (this.checkIsResultOpened(result) && !read.has(result.email)) {
        // It's been "opened" but the recipient has not yet "read" the post.
        opened.add(result.email);
      }
    });
    let numReadPost = read.size;
    let numOpenedEmail = opened.size;
    let numUnread = total.size - numReadPost - numOpenedEmail;
    return [
      {
        value: 'readPost',
        label: 'Post read in-app',
        count: numReadPost,
        tooltip: `Post read in-app (${numReadPost})`,
      },
      {
        value: 'emailOpened',
        label: 'Email opened',
        count: numOpenedEmail,
        tooltip: `Email opened (${numOpenedEmail})`,
      },
      {
        value: 'unread',
        label: 'Unread',
        count: numUnread,
        tooltip: `Unread (${numUnread})`,
      },
    ];
  }

  // learnerReadChartData calculates how many learners the notifications were read or unread for
  @computed(
    'learnerIds.[]',
    'allReceivedResults.@each.{isRead,person,trackingStatus,recipientType}',
  )
  get learnerReadChartData() {
    if (this.learnerIds.length === 1) {
      // This analysis is for a post for a single kid - no need for the graph
      return;
    }
    // Filter out recipients not associated with learners
    let results = filterBy(
      this.allReceivedResults,
      'recipientType',
      PersonType.CAREGIVER,
    );
    // Create a set for unique values
    let readCount = new Set();
    let openedCount = new Set();
    let totalCount = new Set();
    results.forEach((result) => {
      let isResultRead = this.checkIsResultRead(result);
      let isResultOpened = this.checkIsResultOpened(result);
      let caregiver = result.person;
      let caregiverLearnerIds = caregiver.get('caregiver.learnerIds');
      if (isEmpty(caregiverLearnerIds)) {
        // Skip caregivers with no learners, we're not interested in their stats
        return;
      }
      // Check for only those learners that are included in this post
      caregiverLearnerIds.forEach((learnerId) => {
        if (this.learnerIds.includes(learnerId)) {
          // They're included - decide where to add them
          // while avoiding doubling up on the learners (i.e. the same learner has a caregiver with 'unread'
          // and a 'read', then we only want to count the read for that learner)
          totalCount.add(learnerId);
          if (isResultRead) {
            readCount.add(learnerId);
            // Remove learner from "opened" list, to avoid double-counting.
            openedCount.delete(learnerId);
          }
          // Only add learner to "opened" list if not already in "read" list.
          if (isResultOpened && !readCount.has(learnerId)) {
            openedCount.add(learnerId);
          }
        }
      });
    });
    let unreadCount = totalCount.size - readCount.size - openedCount.size;
    return [
      {
        value: 'readPost',
        label: 'Post read for students',
        count: readCount.size,
        tooltip: `Post was read by at least one caregiver for ${pluralize(
          readCount.size,
          'student',
        )}`,
      },
      {
        value: 'openedPost',
        label: 'Email opened for students',
        count: openedCount.size,
        tooltip: `Email was opened by at least one caregiver for ${pluralize(
          openedCount.size,
          'student',
        )}`,
      },
      {
        value: 'unread',
        label: 'Unread for students',
        count: unreadCount,
        tooltip: `Caregivers did not read the post or email for ${pluralize(
          unreadCount,
          'student',
        )}`,
      },
    ];
  }

  @computed('post.{postType,person_id,groups}')
  get learnerPersons() {
    if (this.post.postType === PostType.POST) {
      return [this.post.person];
    }

    let groups = resolved(this.post.groups);
    let rawList = groups.reduce((learnerPersons, group) => {
      if (!group.membershipHidden) {
        learnerPersons.push(...mapBy(group.currentLearners, 'person'));
        // Also push former group members in, as they might have left after the notification was sent.
        learnerPersons.push(
          ...mapBy(filterBy(group.learners, 'status', 2), 'person'),
        );
      }
      return learnerPersons;
    }, []);
    // compact() will clear the array of any null or undefined that might've been pushed from groups
    return uniqBy(compact(rawList), 'id');
  }

  @computed('post.{postType,person_id,groups}')
  get currentMembersLearnerPersons() {
    if (this.post.postType === PostType.POST) {
      return [this.post.person];
    }

    let groups = resolved(this.post.groups);
    let rawList = groups.reduce((learnerPersons, group) => {
      if (!group.membershipHidden) {
        learnerPersons.push(...mapBy(group.currentLearners, 'person'));
      }
      return learnerPersons;
    }, []);
    // compact() will clear the array of any null or undefined that might've been pushed from groups
    return uniqBy(compact(rawList), 'id');
  }

  @computed('learnerPersons.[]')
  get learnerIds() {
    return mapBy(this.learnerPersons, 'id');
  }

  // rawStaffRows can be used as data charts or other required manipulations
  @computed('notificationResults.@each.personType')
  get rawStaffRows() {
    let results = filterBy(
      rejectBy(this.notificationResults, 'transportType', 'unspecified'),
      'personType',
      PersonType.STAFF,
    );
    if (isEmpty(results)) {
      return [];
    }
    let staff = [];

    results.forEach((result) => {
      let staffPerson = result.get('person');
      // Check if a staff member is already in the staff array
      let staffEntry = staff.find(
        (entry) => entry.person.get('id') === staffPerson.get('id'),
      );
      if (staffEntry) {
        // Add the result to the existent staff entry
        this.pushNotificationResult(staffEntry.results, result);
        return;
      }
      // No staff entry - gather enough info to create one
      let email = staffPerson.get('fields.core:emailAddress.valueString');
      staffEntry = {
        person: staffPerson,
        email,
        sent: result.createTime, // Initialize with the first sent time
        results: {},
      };
      staff.push(staffEntry);
      // Add the current result to the results array of the now existing staff entry
      this.pushNotificationResult(staffEntry.results, result);
    });

    return staff;
  }

  // displayedStaffRows stringifies and simplifies arrays and objects within rawStaffRows
  // for the ease of future manipulating of and displaying them
  @computed('rawStaffRows.[]')
  get displayedStaffRows() {
    // Create a shallow top-level copy
    return this.rawStaffRows.map((row) => {
      // Create a deep copy of the row's results object
      let resultsCopy = { ...row.results };
      // Simplify and stringify push and email results so we can filter against them
      if (!isEmpty(resultsCopy.email)) {
        resultsCopy.email = resultsCopy.email
          .map((result) => result.readStatus)
          .join('<br>');
      }
      if (!isEmpty(resultsCopy.mobilePush)) {
        // Create a deep copy of referenced objects that we can modify
        resultsCopy.mobilePush = resultsCopy.mobilePush
          .map((result) => result.readStatus)
          .join('<br>');
      }
      // Return a new row copy with modified results
      return { ...row, results: resultsCopy };
    });
  }

  @computed(
    'displayedStaffRows.[]',
    'internalRecipientsFilters.@each.{field,value}',
  )
  get filteredStaffRows() {
    if (isEmpty(this.internalRecipientsFilters)) {
      return this.displayedStaffRows;
    }
    // Create a shallow top-level copy
    let newArray = this.displayedStaffRows.slice();
    return newArray.filter((row) => {
      // Apply filters
      return this.internalRecipientsFilters.every((filter) => {
        return filterValue(
          get(row, filter.field) ?? '',
          DataType.STRING,
          filter.value,
        );
      });
    });
  }

  @computed('notificationResults.@each.personType', 'learnerIds.[]')
  get caregiverRows() {
    let results = filterBy(
      rejectBy(this.notificationResults, 'transportType', 'unspecified'),
      'personType',
      PersonType.CAREGIVER,
    );

    let caregivers = [];

    results.forEach((result) => {
      let caregiver = result.get('person');
      // Check if caregiver is already in the caregivers array
      let caregiverEntry = caregivers.find(
        (entry) => entry.person.get('id') === caregiver.get('id'),
      );
      if (caregiverEntry) {
        // Add the result to the existent caregiver entry
        this.pushNotificationResult(caregiverEntry.results, result);
        return;
      }
      // No caregiver entry - gather enough info to create one
      let caregiverLearnerIds = caregiver.get('caregiver.learnerIds');
      if (isEmpty(caregiverLearnerIds)) {
        // Skip caregivers with no learners, we're not interested in their stats
        return;
      }

      let learners = [];
      if (!isEmpty(caregiverLearnerIds)) {
        caregiverLearnerIds.forEach((learnerId) => {
          if (this.learnerIds.includes(learnerId)) {
            learners.push(this.store.peekRecord('person', learnerId));
          }
        });
      }
      let email = caregiver.get('fields.core:emailAddress.valueString');
      caregiverEntry = {
        person: caregiver,
        email,
        learners,
        sent: result.createTime, // Initialize with the first sent time
        results: {},
      };
      caregivers.push(caregiverEntry);
      // Add the current result to the results array of the now existing caregiver entry
      this.pushNotificationResult(caregiverEntry.results, result);
    });

    return caregivers;
  }

  @computed('caregiverRows.[]')
  get displayedCaregiverRows() {
    return this.caregiverRows.map((row) => {
      let learnersCopy = [...row.learners];
      // Create a deep copy of the row's results object
      let resultsCopy = { ...row.results };
      // Simplify and stringify students, push and email results so we can filter against them
      if (!isEmpty(learnersCopy)) {
        learnersCopy = learnersCopy
          // We only display their full name
          .map((learner) => learner.get('fullName'))
          .join('<br>');
      }
      if (!isEmpty(resultsCopy.email)) {
        resultsCopy.email = resultsCopy.email
          .map((result) => result.readStatus)
          .join('<br>');
      }
      if (!isEmpty(resultsCopy.mobilePush)) {
        // Create a deep copy of referenced objects that we can modify
        resultsCopy.mobilePush = resultsCopy.mobilePush
          .map((result) => result.readStatus)
          .join('<br>');
      }
      // Return a new row copy with modified results and learners
      return { ...row, learners: learnersCopy, results: resultsCopy };
    });
  }

  @computed(
    'displayedCaregiverRows.[]',
    'externalRecipientsFilters.@each.{field,value}',
  )
  get filteredCaregiverRows() {
    if (isEmpty(this.externalRecipientsFilters)) {
      return this.displayedCaregiverRows;
    }
    // Create a shallow top-level copy
    let newArray = this.displayedCaregiverRows.slice();
    return newArray.filter((row) => {
      // Apply filters
      return this.externalRecipientsFilters.every((filter) => {
        return filterValue(
          get(row, filter.field) ?? '',
          DataType.STRING,
          filter.value,
        );
      });
    });
  }

  @computed(
    'notificationResults.@each.{personType,transportType}',
    'learnerIds.[]',
  )
  get learnerRows() {
    let results = filterBy(
      rejectBy(this.notificationResults, 'transportType', 'unspecified'),
      'personType',
      PersonType.CAREGIVER,
    );

    let learners = [];

    results.forEach((result) => {
      let isResultRead = this.checkIsResultRead(result);
      let isResultOpened = this.checkIsResultOpened(result);
      let caregiver = result.get('person');
      let caregiverLearnerIds = caregiver.get('caregiver.learnerIds');

      // Skip caregivers with no learners or learners not in the learnerIds array
      if (isEmpty(caregiverLearnerIds)) {
        return;
      }

      caregiverLearnerIds.forEach((learnerId) => {
        if (this.learnerIds.includes(learnerId)) {
          let learner = this.store.peekRecord('person', learnerId);

          // Find if the learner is already in the learners array
          let learnerEntry = learners.find(
            (entry) => entry.learner.get('id') === learnerId,
          );

          // Initialize the learner entry if it doesn't exist
          if (!learnerEntry) {
            learnerEntry = {
              learner,
              status: 'Unread for student',
              caregivers: [],
            };
            learners.push(learnerEntry);
          }

          if (
            (learnerEntry.status === 'Unread for student' && isResultOpened) ||
            isResultRead
          ) {
            // The actual status is different from the default - update it
            if (isResultRead) {
              learnerEntry.status = 'Post Read for student';
            }
            if (isResultOpened) {
              learnerEntry.status = 'Email Opened for student';
            }
          }

          // Find if the caregiver is already listed for this learner
          let caregiverEntry = learnerEntry.caregivers.find(
            (entry) => entry.person.get('id') === caregiver.get('id'),
          );

          // If the caregiver already exists, add the new result to their entry
          if (caregiverEntry) {
            this.pushNotificationResult(caregiverEntry.results, result);
          } else {
            // No caregiver entry yet for this learner - create a new one
            let email = caregiver.get('fields.core:emailAddress.valueString');
            caregiverEntry = {
              person: caregiver,
              email,
              sent: result.createTime, // Initialize with the first sent time
              results: {},
            };

            // Add the notification result to the caregiver entry
            this.pushNotificationResult(caregiverEntry.results, result);

            // Add the caregiver to the learner's caregivers array
            learnerEntry.caregivers.push(caregiverEntry);
          }
        }
      });
    });

    return learners;
  }

  @computed(
    'externalRecipientLearnerFilters.@each.{field,value}',
    'learnerRows.[]',
  )
  get filteredLearnerRows() {
    if (isEmpty(this.externalRecipientLearnerFilters)) {
      return this.learnerRows;
    }
    return this.learnerRows.filter((row) => {
      // Apply filters
      return this.externalRecipientLearnerFilters.every((filter) => {
        return filterValue(
          get(row, filter.field) ?? '',
          DataType.STRING,
          filter.value,
        );
      });
    });
  }

  @computed(
    'post.{postType,groups}',
    'allReceivedResults.@each.person',
    'currentMembersLearnerPersons.[]',
  )
  get unnotifiedCaregiverRows() {
    let notifiedIds = this.allReceivedResults.map((result) => {
      return result.person.get('id');
    });
    let results = {};

    // Loop through uniquified array of learners
    // This has the bonus that we can identify learners for PENDING caregivers
    this.currentMembersLearnerPersons.forEach((learnerPerson) => {
      learnerPerson.get('learner').primaryContacts.forEach((contact) => {
        // Filter out PENDING or broken caregivers
        if (contact.get('caregiverId')) {
          // Filter out caregivers that were sent notifications
          if (!notifiedIds.includes(contact.get('caregiverId'))) {
            let caregiver = this.store.peekRecord(
              'person',
              contact.get('caregiverId'),
            );
            if (caregiver === null) {
              // Avoid the screen exploding if we have a broken contact
              return;
            }
            let reasons = this.getReasonsForUnnotifiedContact(
              contact,
              caregiver,
            );
            if (isEmpty(results[caregiver.id])) {
              // If we haven't seen this caregiver via a different student, add them
              results[caregiver.id] = {
                fullName: caregiver.get('fullName'),
                email: caregiver.get('fields.core:emailAddress.valueString'),
                learners: [learnerPerson],
                reasons,
              };
            } else {
              // If we have already seen this caregiver, just add this student to their list
              results[caregiver.id].learners.push(learnerPerson);
            }
          }
        }
      });
    });

    return Object.values(results);
  }

  @computed('unnotifiedCaregiverRows.[]')
  get displayedUnnotifiedCaregiverRows() {
    return this.unnotifiedCaregiverRows.map((row) => {
      let learnersCopy = [...row.learners];
      // Create a deep copy of the row's reasons object
      let reasonsCopy = [...row.reasons];
      // Simplify and stringify students, push and email results so we can filter against them
      if (!isEmpty(learnersCopy)) {
        learnersCopy = learnersCopy
          // We only display their full name
          .map((learner) => learner.get('fullName'))
          .join('<br>');
      }
      if (!isEmpty(reasonsCopy)) {
        reasonsCopy = reasonsCopy.join('<br>');
      }

      // Return a new row copy with modified reasons and learners
      return { ...row, learners: learnersCopy, reasons: reasonsCopy };
    });
  }

  @computed(
    'externalNonRecipientFilters.@each.{field,value}',
    'displayedUnnotifiedCaregiverRows.[]',
  )
  get filteredUnnotifiedCaregiverRows() {
    if (isEmpty(this.externalNonRecipientFilters)) {
      return this.displayedUnnotifiedCaregiverRows;
    }
    return this.displayedUnnotifiedCaregiverRows.filter((row) => {
      // Apply filters
      return this.externalNonRecipientFilters.every((filter) => {
        return filterValue(
          get(row, filter.field) ?? '',
          DataType.STRING,
          filter.value,
        );
      });
    });
  }

  @computed(
    'post.{postType,groups}',
    'allReceivedResults.@each.person',
    'currentMembersLearnerPersons.[]',
    'learnerRows.[]',
  )
  get unnotifiedCaregiverLearnersRows() {
    let notifiedIds = this.allReceivedResults.map((result) => {
      return result.person.get('id');
    });
    let results = [];

    // Loop through uniquified array of learners
    // This has the bonus that we can identify learners for PENDING caregivers
    this.currentMembersLearnerPersons.forEach((learnerPerson) => {
      // Find if the learner is already in the learners array
      let learnerEntry = results.find(
        (entry) => entry.learnerPerson.get('id') === learnerPerson.get('id'),
      );

      // Initialize the learner entry if it doesn't exist
      if (!learnerEntry) {
        learnerEntry = {
          learnerPerson,
          caregivers: [],
        };
      }
      // Iterate over the learner's contacts and caregivers
      learnerPerson.get('learner').primaryContacts.forEach((contact) => {
        // Filter out PENDING or broken caregivers
        if (contact.get('caregiverId')) {
          // Filter out caregivers that were sent notifications
          if (!notifiedIds.includes(contact.get('caregiverId'))) {
            let caregiver = this.store.peekRecord(
              'person',
              contact.get('caregiverId'),
            );
            if (caregiver === null) {
              // Avoid the screen exploding if we have a broken contact
              return;
            }
            let reasons = this.getReasonsForUnnotifiedContact(
              contact,
              caregiver,
            );
            // If we haven't seen this caregiver for this student, add them
            learnerEntry.caregivers.push({
              fullName: caregiver.get('fullName'),
              email: caregiver.get('fields.core:emailAddress.valueString'),
              reasons,
            });
          }
        }
      });
      let receiversLearnerEntry = this.learnerRows.find(
        (entry) => entry.learner.get('id') === learnerPerson.get('id'),
      );
      if (receiversLearnerEntry && isEmpty(learnerEntry.caregivers)) {
        // Skip learners that have already appeared in received notifications,
        // and they have no non-receivers contacts here - they don't qualify
        return;
      }
      // Finally, push the learner entry to the results
      results.push(learnerEntry);
    });

    return Object.values(results);
  }

  @computed(
    'externalNonRecipientLearnerFilters.@each.{field,value}',
    'unnotifiedCaregiverLearnersRows.[]',
  )
  get filteredUnnotifiedCaregiverLearnerRows() {
    if (isEmpty(this.externalNonRecipientLearnerFilters)) {
      return this.unnotifiedCaregiverLearnersRows;
    }
    return this.unnotifiedCaregiverLearnersRows.filter((row) => {
      // Apply filters
      return this.externalNonRecipientLearnerFilters.every((filter) => {
        return filterValue(
          get(row, filter.field) ?? '',
          DataType.STRING,
          filter.value,
        );
      });
    });
  }

  @computed('post.updateTime')
  get postPredatesEmailTracking() {
    return moment.unix(this.post.updateTime).isBefore(moment('2020-10-24'));
  }

  @computed(
    'post.postType',
    'session.school.{emailNotificationsGlobalEnabled,emailNotificationsPostsEnabled,emailNotificationsCommunityNoticesEnabled}',
  )
  get emailNotificationsEnabled() {
    switch (this.post.postType) {
      case PostType.POST:
        return this.session.school.emailNotificationsPostsEnabled;
      case PostType.COMMUNITY_NOTICE:
        return this.session.school.emailNotificationsCommunityNoticesEnabled;
      // todo: add more types when we support the analysis for more posts
      default:
        return this.session.school.emailNotificationsGlobalEnabled;
    }
  }

  @computed(
    'post.postType',
    'session.school.{pushNotificationsGlobalEnabled,pushNotificationsPostsEnabled,pushNotificationsCommunityNoticesEnabled}',
  )
  get pushNotificationsEnabled() {
    switch (this.post.postType) {
      case PostType.POST:
        return this.session.school.pushNotificationsPostsEnabled;
      case PostType.COMMUNITY_NOTICE:
        return this.session.school.pushNotificationsCommunityNoticesEnabled;
      // todo: add more types when we support the analysis for more posts
      default:
        return this.session.school.pushNotificationsGlobalEnabled;
    }
  }

  // Filtering
  // internalRecipientFilterFields are used when we filter on internal recipients table
  internalRecipientFilterFields = Object.freeze([
    'person.fullName',
    'results.mobilePush',
    'results.email',
    'email',
    'sent',
  ]);

  @tracked
  internalRecipientsFilters = [];

  // internalRecipientFilterFields are used when we filter on internal recipients table
  externalRecipientFilterFields = Object.freeze([
    'person.fullName',
    'learners',
    'results.mobilePush',
    'results.email',
    'email',
    'sent',
  ]);

  @tracked
  externalRecipientsFilters = [];
  @tracked
  externalRecipientLearnerFilters = [];

  externalNonRecipientFilterFields = Object.freeze([
    'fullName',
    'learners',
    'reasons',
    'email',
  ]);

  @tracked
  externalNonRecipientFilters = [];
  @tracked
  externalNonRecipientLearnerFilters = [];

  // For group makers
  @tracked
  selectedRecipientLearners = [];

  @tracked
  selectedNonRecipientLearners = [];

  // Functions
  readStatus(result) {
    // status is set to ResultStatus.PERMISSION_DENIED if user is denied access to a post
    if (result.status === ResultStatus.PERMISSION_DENIED) {
      return `Permission Denied`;
    }
    // Cover any other type-specific tracking statuses that are updated by third-party
    if (isPushNotification(result.transportType)) {
      return this.readStatusForPush(result);
    }
    if (isEmailNotification(result.transportType)) {
      return this.readStatusForEmail(result);
    }
  }

  readStatusForEmail(result) {
    // Check the result status and our internal tracking first
    if (result.status === ResultStatus.SUCCESS) {
      // The email got delivered
      if (result.isRead) {
        // And read by the user in-app
        let updateDate =
          ' (' +
          moment.unix(result.updateTime).format('Do MMMM, YYYY HH:mm:ss') +
          ')';
        return `Post Read${updateDate}`;
      }
    }
    // They either didn't receive it or didn't open it in-app. Let's check
    let trackingTimeUnix, trackingDate;
    switch (result.trackingStatus) {
      // Means they've opened the email
      case TrackingStatus.RECEIVED:
        trackingTimeUnix = result.get('trackingInfo.trackingTimeUnix');
        if (!isEmpty(trackingTimeUnix)) {
          trackingDate =
            ' (' +
            moment.unix(trackingTimeUnix).format('Do MMMM, YYYY HH:mm:ss') +
            ')';
        }
        return `Email Opened${trackingDate}`;
      case TrackingStatus.UNSUBSCRIBED:
        return `Unsubscribed from receiving emails`;
      case TrackingStatus.BOUNCED:
        // Mandrill-specific tracking errors
        if (result.trackingError === 'soft-bounce') {
          return 'Delayed delivery - temporary issue';
        }
        if (result.trackingError === 'hard-bounce') {
          return 'Failed to deliver - invalid address';
        }
        return 'Failed to deliver - bounced';
      case TrackingStatus.SPAM:
        return `Marked as Spam`;
      case TrackingStatus.REJECTED:
        return `Failed to deliver - rejected by recipient`;
      case TrackingStatus.FAILED:
        return `Failed to deliver - technical issue`;
      default:
        // Otherwise it probably hasn't been read
        return 'Unread';
    }
  }

  readStatusForPush(result) {
    // Check the result status and our internal tracking first
    if (result.status === ResultStatus.SUCCESS) {
      // The push got delivered
      if (result.isRead) {
        // And read by the user in-app
        let updateDate =
          ' (' +
          moment.unix(result.updateTime).format('Do MMMM, YYYY HH:mm:ss') +
          ')';
        return `Post Read${updateDate}`;
      }
    }
    // They either didn't receive it or didn't open it in-app. Let's check
    switch (result.trackingStatus) {
      // FCM- and a bit of gaurun-specific messages
      case TrackingStatus.BOUNCED:
      case TrackingStatus.UNSUBSCRIBED:
        return 'Unsubscribed from receiving push notifications';
      case TrackingStatus.REJECTED:
      case TrackingStatus.FAILED:
        return `Failed to deliver - technical issue`;
      default:
        // Otherwise it probably hasn't been read
        return 'Unread';
    }
  }

  pushNotificationResult(resultsArray, result) {
    // Find the type from the enum
    let transportType = NotificationTransportType.values.find(
      (notificationTransport) =>
        notificationTransport.value === result.transportType,
    ).transport;
    if (!resultsArray[transportType]) {
      // Initialize the transport array if it doesn't exist
      resultsArray[transportType] = [];
    }
    // Push the result into the appropriate transport array
    resultsArray[transportType].push({
      readStatus: this.readStatus(result),
    });
  }

  getReasonsForUnnotifiedContact(contact, caregiver) {
    // We do not store reasons for not sending, so we can only speculate what went wrong
    // judging by the settings at the time of viewing the analysis
    let reasons = [];
    // School-wide reasons
    if (!this.emailNotificationsEnabled) {
      reasons.push('School disabled e-mail notifications');
    }
    if (!this.pushNotificationsEnabled) {
      reasons.push('School disabled in-app notifications');
    }
    // Personal reasons
    if (!contact.allowedAccess) {
      reasons.push('Access disabled');
    }
    if (isEmpty(caregiver.get('fields.core:emailAddress.valueString'))) {
      reasons.push('No email address');
    }
    if (
      this.personOptions.getNotificationPreference(caregiver) ===
      PersonNotificationPreference.NONE
    ) {
      reasons.push('Disabled notifications');
    }
    if (isEmpty(reasons)) {
      reasons.push('Unknown');
    }
    return reasons;
  }

  filter(filters, field, value) {
    let filter = findBy(filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      return [...filters, { field, value }];
    }
    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      return filters.filter((f) => f !== filter);
    }
    // Existing filter has been changed; update it.
    set(filter, 'value', value);
    return filters;
  }

  // checkIsResultRead is a util function that performs a check for the read post
  checkIsResultRead(result) {
    return result.isRead;
  }

  // checkIsResultRead is a util function that performs a check for the email opened
  checkIsResultOpened(result) {
    return result.trackingStatus === TrackingStatus.RECEIVED;
  }

  toggleSelected(selectedArray, object) {
    if (selectedArray.includes(object)) {
      return selectedArray.filter((o) => o !== object);
    }
    return [...selectedArray, object];
  }

  reset() {
    this.selectedRecipientLearners = [];
    this.selectedNonRecipientLearners = [];
    this.internalRecipientsFilters = [];
    this.externalRecipientsFilters = [];
    this.externalRecipientLearnerFilters = [];
    this.externalNonRecipientFilters = [];
    this.externalNonRecipientLearnerFilters = [];
  }

  // Component hooks
  didDestroyElement() {
    super.didDestroyElement(...arguments);
    this.reset();
  }

  // filterInternalRecipients toggles a field to be filtered by for internal recipients
  @action
  filterInternalRecipients(field, value) {
    this.internalRecipientsFilters = this.filter(
      this.internalRecipientsFilters,
      field,
      value,
    );
  }

  // filterExternalRecipients toggles a field to be filtered by for external recipients in the caregivers view
  @action
  filterExternalRecipients(field, value) {
    this.externalRecipientsFilters = this.filter(
      this.externalRecipientsFilters,
      field,
      value,
    );
  }

  // filterExternalRecipientsLearners toggles a field to be filtered by for external recipients in the learners view
  @action
  filterExternalRecipientsLearners(field, value) {
    this.externalRecipientLearnerFilters = this.filter(
      this.externalRecipientLearnerFilters,
      field,
      value,
    );
  }

  // filterExternalNonRecipients toggles a field to be filtered by for external non-recipients in the caregivers view
  @action
  filterExternalNonRecipients(field, value) {
    this.externalNonRecipientFilters = this.filter(
      this.externalNonRecipientFilters,
      field,
      value,
    );
  }

  // filterExternalNonRecipientsLearners toggles a field to be filtered by for external non-recipients in the learners view
  @action
  filterExternalNonRecipientsLearners(field, value) {
    this.externalNonRecipientLearnerFilters = this.filter(
      this.externalNonRecipientLearnerFilters,
      field,
      value,
    );
  }

  // switchExternalRecipientsView switches the view of the external recipients table
  @action
  switchExternalRecipientsView(mode) {
    if (mode !== this.externalRecipientsView) {
      this.externalRecipientsView = mode;
    }
  }

  // switchExternalNonRecipientsView switches the view of the external non-recipients table
  @action
  switchExternalNonRecipientsView(mode) {
    if (mode !== this.nonRecipientsView) {
      this.nonRecipientsView = mode;
    }
  }

  @action
  toggleAllRecipients() {
    let selectedArray = this.selectedRecipientLearners;
    if (selectedArray.length >= this.filteredLearnerRows.length) {
      // All or more are already selected, so
      // clear the selection.
      this.selectedRecipientLearners = [];
      return;
    }
    // Not all selected; select them all.
    let learners = mapBy(this.filteredLearnerRows, 'learner');
    this.selectedRecipientLearners = learners;
  }

  @action
  toggleAllNonRecipientLearners() {
    let selectedArray = this.selectedNonRecipientLearners;
    if (
      selectedArray.length >= this.filteredUnnotifiedCaregiverLearnerRows.length
    ) {
      // All or more are already selected, so
      // clear the selection.
      this.selectedNonRecipientLearners = [];
      return;
    }
    // Not all selected; select them all.
    let learners = mapBy(
      this.filteredUnnotifiedCaregiverLearnerRows,
      'learnerPerson',
    );
    this.selectedNonRecipientLearners = learners;
  }

  @action
  toggleSelectedRecipient(learner) {
    this.selectedRecipientLearners = this.toggleSelected(
      this.selectedRecipientLearners,
      learner,
    );
  }

  @action
  toggleSelectedNonRecipient(learner) {
    this.selectedNonRecipientLearners = this.toggleSelected(
      this.selectedNonRecipientLearners,
      learner,
    );
  }
}
